import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  DefaultRates,
  InviteStaffInput,
  RateCardEntry,
  SetDefaultRateInput,
  SetStaffRateInput,
  StaffInvite,
  StaffList,
  StaffMember,
  StaffRates,
  UpdateStaffInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  getDefaultRates,
  getStaffMember,
  getStaffRates,
  inviteStaff,
  listInvites,
  listStaff,
  revokeInvite,
  setDefaultRate,
  setStaffActive,
  setStaffRate,
  updateStaffMember,
  type RateParams,
} from './staff-api.js';

/**
 * The roster's data layer.
 *
 * Nothing here goes through the offline write queue, which is a deliberate difference from
 * payments. Taking cash in a basement changing room must survive a dead connection; inviting a
 * karigar or changing a rate is a seated, deliberate act, and one that would be actively
 * harmful to apply silently twenty minutes later — a rate that quietly lands after work has
 * been assigned at the old one is the exact dispute the dating rules exist to prevent.
 *
 * Setting a rate invalidates BOTH grids: a shop default changes what every person who has no
 * override of their own resolves to, so their screens are stale the moment it lands.
 */

export const staffKeys = {
  all: ['staff'] as const,
  list: () => ['staff', 'list'] as const,
  detail: (userId: string) => ['staff', 'detail', userId] as const,
  invites: () => ['staff', 'invites'] as const,
  rates: (userId: string, params: RateParams) => ['staff', 'rates', userId, params] as const,
  defaultRates: (params: RateParams) => ['staff', 'rates', 'defaults', params] as const,
};

export function useStaffList(): UseQueryResult<StaffList> {
  const client = useApiClient();
  return useQuery({ queryKey: staffKeys.list(), queryFn: () => listStaff(client) });
}

export function useStaffMember(userId: string | null): UseQueryResult<StaffMember> {
  const client = useApiClient();
  return useQuery({
    queryKey: staffKeys.detail(userId ?? ''),
    queryFn: () => getStaffMember(client, userId as string),
    enabled: userId !== null,
  });
}

export function useStaffInvites(
  options: { enabled?: boolean } = {},
): UseQueryResult<StaffInvite[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: staffKeys.invites(),
    queryFn: () => listInvites(client),
    enabled: options.enabled ?? true,
  });
}

export function useInviteStaff(): UseMutationResult<StaffInvite, unknown, InviteStaffInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteStaffInput) => inviteStaff(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

export function useRevokeInvite(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => revokeInvite(client, inviteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: staffKeys.invites() });
    },
  });
}

export function useUpdateStaffMember(
  userId: string,
): UseMutationResult<StaffMember, unknown, UpdateStaffInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateStaffInput) => updateStaffMember(client, userId, patch),
    onSuccess: (member) => {
      queryClient.setQueryData(staffKeys.detail(userId), member);
      void queryClient.invalidateQueries({ queryKey: staffKeys.list() });
    },
  });
}

export function useSetStaffActive(): UseMutationResult<
  StaffMember,
  unknown,
  { userId: string; active: boolean }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
      setStaffActive(client, userId, active),
    onSuccess: (member) => {
      queryClient.setQueryData(staffKeys.detail(member.userId), member);
      void queryClient.invalidateQueries({ queryKey: staffKeys.list() });
    },
  });
}

export function useStaffRates(
  userId: string | null,
  params: RateParams = {},
): UseQueryResult<StaffRates> {
  const client = useApiClient();
  return useQuery({
    queryKey: staffKeys.rates(userId ?? '', params),
    queryFn: () => getStaffRates(client, userId as string, params),
    enabled: userId !== null,
  });
}

export function useDefaultRates(
  params: RateParams = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<DefaultRates> {
  const client = useApiClient();
  return useQuery({
    queryKey: staffKeys.defaultRates(params),
    queryFn: () => getDefaultRates(client, params),
    enabled: options.enabled ?? true,
  });
}

export function useSetDefaultRate(): UseMutationResult<
  RateCardEntry,
  unknown,
  SetDefaultRateInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetDefaultRateInput) => setDefaultRate(client, input),
    onSuccess: () => {
      // Everyone without an override of their own just moved with it.
      void queryClient.invalidateQueries({ queryKey: ['staff', 'rates'] });
    },
  });
}

export function useSetStaffRate(
  userId: string,
): UseMutationResult<RateCardEntry, unknown, SetStaffRateInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetStaffRateInput) => setStaffRate(client, userId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', 'rates'] });
    },
  });
}
